import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import type { AccountInfo } from '../features/settings';
import { useInformationQuery } from '../features/settings';
import ArrowIcon from '../images/arrow-right.svg?react';
import LogoutIcon from '../images/exit.png';
import HelpIcon from '../images/help.svg?react';
import logo from '../images/logo.png';
import OverviewIcon from '../images/overview.svg?react';
import PendingIcon from '../images/pending.svg?react';
import RssIcon from '../images/rss.svg?react';
import SettingIcon from '../images/setting.svg?react';
import UploadedIcon from '../images/uploaded.svg?react';
import closeSidebarIcon from '/close-sidebar.svg';
import openSidebarIcon from '/open-sidebar.svg';

const LightTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
    ({ theme }: any) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.common.white,
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: theme.shadows[3],
            fontSize: 15,
        },
    })
);

const NO_ACCOUNT: AccountInfo = {};

function MenuBar({ color, isMobile = false, desktopExpanded = false, onDesktopToggle, open, onClose }: any) {
    const { t } = useTranslation();
    const { data: info = NO_ACCOUNT } = useInformationQuery();
    const location = useLocation();
    const activePath = location.pathname;
    const isDark = color === 'dark';
    const isExpanded = !isMobile && desktopExpanded;

    // Compose navigation items with label + icon metadata so UI and behavior stay in one place.
    const menuItems = useMemo(
        () => [
            { id: 'overview', label: t('Overview.title'), to: '/overview', icon: OverviewIcon },
            { id: 'pending', label: t('Pending.title'), to: '/pending', icon: PendingIcon },
            { id: 'uploaded', label: t('Records.title'), to: '/uploaded', icon: UploadedIcon },
            { id: 'realtime', label: t('RealTime.title'), to: '/realtime', icon: RssIcon },
            { id: 'setting', label: t('Information.title'), to: '/setting', icon: SettingIcon },
        ],
        [t]
    );

    const isControlled = typeof open === 'boolean';
    const menuOpen = isControlled ? open : false;

    const handleClose = () => {
        if (isControlled && onClose) {
            onClose();
        }
    };

    const handleLinkClick = () => {
        if (isMobile) {
            handleClose();
        }
    };

    // The backdrop is decorative and hidden from assistive tech, so Escape is the
    // only way a keyboard user can dismiss the open mobile menu.
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isControlled && onClose) onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [menuOpen, isControlled, onClose]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/login';
    };

    // Basic user information shown on the mobile drawer.
    const userName = info?.display_name || info?.name || 'User Name';
    const userEmail = info?.email || 'sales@gocochain.com';
    const badge = info?.company_group || 'General';
    const initials = userName
        .split(' ')
        .filter(Boolean)
        .map((part: any) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    const helpLabel = t('Help.title');
    const logoutLabel = t('Login.logout');
    const mainMenuLabel = t('Menu.main');

    // Container classes handle both desktop rail and mobile drawer.
    const containerClasses = [
        'fixed top-0 left-0 h-screen flex flex-col z-50 overflow-hidden',
        isDark ? 'bg-slate-900 text-slate-100' : 'bg-white text-slate-700',
        isMobile ? 'w-72 px-6 py-6 transition-transform duration-300' : 'py-6 transition-all duration-300 ease-in-out',
        isMobile ? (menuOpen ? 'translate-x-0' : '-translate-x-full') : '',
        !isMobile && isExpanded ? 'items-start' : '',
        !isMobile && !isExpanded ? 'items-center' : '',
    ]
        .filter(Boolean)
        .join(' ');

    // Smooth width/padding animation for desktop expand/collapse.
    const containerStyle = isMobile
        ? undefined
        : {
              width: isExpanded ? 320 : 80,
              paddingLeft: isExpanded ? 24 : 0,
              paddingRight: isExpanded ? 24 : 0,
              transition: 'width 300ms ease, padding 300ms ease',
          };

    const userNameColor = isDark ? 'text-white' : 'text-slate-900';
    const userEmailColor = isDark ? 'text-slate-300' : 'text-slate-500';

    // Render a single menu entry (icon + label) and highlight it when route matches.
    const renderMenuItem = (item: any) => {
        const Icon = item.icon;
        const isActive = activePath === item.to;
        const baseClasses = [
            'flex items-center transition-colors',
            isMobile || isExpanded
                ? 'gap-3 rounded-full px-4 py-4 text-[16px] font-medium w-full'
                : 'justify-center w-12 h-12 rounded-2xl',
            isActive ? 'bg-primary text-white' : isDark ? 'text-slate-200' : 'text-slate-500',
        ]
            .filter(Boolean)
            .join(' ');

        const iconColor = isActive ? '#FFFFFF' : isDark ? '#E2E8F0' : '#64748B';
        const content = (
            <div className={baseClasses}>
                <Icon className='h-[16px] w-[16px] flex-shrink-0' style={{ color: iconColor }} />
                {(isMobile || isExpanded) && <span>{item.label}</span>}
            </div>
        );

        const tooltipTitle = !isMobile && !isExpanded ? item.label : undefined;

        return item.external ? (
            <LightTooltip key={item.id} title={tooltipTitle} placement='right'>
                <a
                    key={item.id}
                    href={item.to}
                    target='_blank'
                    rel='noreferrer'
                    className='w-full'
                    onClick={handleLinkClick}
                >
                    {content}
                </a>
            </LightTooltip>
        ) : (
            <LightTooltip key={item.id} title={tooltipTitle} placement='right'>
                <Link key={item.id} to={item.to} className='w-full' onClick={handleLinkClick}>
                    {content}
                </Link>
            </LightTooltip>
        );
    };

    return (
        <>
            <div className={containerClasses} style={containerStyle}>
                {!isMobile ? (
                    <div
                        className={`flex w-full items-center ${isExpanded ? 'justify-between' : 'justify-center'} px-2`}
                    >
                        {isExpanded && (
                            <div className='flex items-center gap-2'>
                                <img
                                    src={logo}
                                    alt='Gocochain logo'
                                    className='h-10 w-10 rounded-full object-contain'
                                />
                                <p className='text-xs font-semibold uppercase text-[#919191]'>{mainMenuLabel}</p>
                            </div>
                        )}
                        <button
                            type='button'
                            onClick={onDesktopToggle}
                            className='flex h-10 w-10 items-center justify-center rounded-md  hover:bg-gray-100 cursor-pointer'
                            aria-label={isExpanded ? 'Close sidebar' : 'Open sidebar'}
                        >
                            <img
                                src={isExpanded ? closeSidebarIcon : openSidebarIcon}
                                alt={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                                className='h-5 w-5'
                            />
                        </button>
                    </div>
                ) : (
                    <div className='flex items-center gap-3'>
                        <img src={logo} alt='Gocochain logo' className='h-10 w-10  md:block hidden' />
                    </div>
                )}

                {isMobile && (
                    <>
                        {/* User summary block */}
                        <div className={`mt-6 flex items-center gap-3 rounded-2xl p-3 `}>
                            <div className='relative flex h-[60px] w-[60px] items-center justify-center rounded-full border border-slate-200 bg-white p-1'>
                                <img
                                    src={logo}
                                    alt='Gocochain logo'
                                    className='h-full w-full rounded-full object-contain'
                                />
                                <span className='absolute -bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-blue-400 bg-[#ECF4FD] px-2 text-[10px] font-medium text-primary z-10'>
                                    {badge}
                                </span>
                            </div>
                            <div>
                                <p className={`text-base font-semibold ${userNameColor}`}>{userName}</p>
                                <p className={`text-sm ${userEmailColor}`}>{userEmail}</p>
                            </div>
                        </div>
                        <div className='border-[0.5px] border-gray-200 h-0 mt-4 block md:hidden' />
                        {/* Main menu label */}
                        <p className='mt-6 text-xs  uppercase  text-[#919191] font-semibold'>{mainMenuLabel}</p>
                    </>
                )}

                {/* Main navigation list */}
                <div
                    className={`mt-4 flex flex-col gap-2 ${isMobile ? 'w-full' : isExpanded ? 'w-full' : 'items-center'}`}
                >
                    {(isMobile ? menuItems.filter((item: any) => item.id !== 'help') : menuItems).map(renderMenuItem)}
                </div>

                {/* Admin badge for desktop */}
                {!isMobile && (
                    <div className='mt-auto w-full px-2'>
                        {isExpanded ? (
                            <>
                                <div className='flex flex-col gap-3'>
                                    <a
                                        href='/download'
                                        target='_blank'
                                        rel='noreferrer'
                                        className='flex items-center justify-between rounded-full border border-gray-300 px-4 py-3 text-[16px] font-medium text-slate-700 hover:bg-gray-50'
                                    >
                                        <div className='flex items-center gap-3'>
                                            <div className='flex h-4 w-4 items-center justify-center rounded-full  border-gray-300 hover:bg-gray-50'>
                                                <img src='/download.svg' alt='' className='h-full w-full' />
                                            </div>
                                            <span>{t('Download.title')}</span>
                                        </div>
                                        <img src='/arrow_top_right.svg' alt='' className='h-4 w-4' />
                                    </a>
                                    <a
                                        href='https://www.gocochain.com/help'
                                        target='_blank'
                                        rel='noreferrer'
                                        className='flex items-center justify-between rounded-full border border-gray-300 px-4 py-3 text-[16px] font-medium text-slate-700 hover:bg-gray-50'
                                    >
                                        <div className='flex items-center gap-3'>
                                            <div className='flex h-4 w-4 items-center justify-center rounded-full border-gray-300 hover:bg-gray-50'>
                                                <img src='/help.svg' alt='' className='h-full w-full' />
                                            </div>
                                            <span>{helpLabel}</span>
                                        </div>
                                        <img src='/arrow_top_right.svg' alt='' className='h-4 w-4' />
                                    </a>
                                </div>
                                <div className='my-4 h-px w-full bg-gray-200' />
                                <div className='flex items-center gap-3'>
                                    <div className='relative flex h-12 w-12 items-center justify-center rounded-full border border-gray-300 bg-white'>
                                        <img src={logo} alt='Gocochain logo' className='h-6 w-6' />
                                        <span
                                            className={`absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 flex h-[18px] w-[53px] items-center justify-center rounded-full border text-[11px] font-semibold ${
                                                info.is_vendor
                                                    ? 'bg-[#FFEEEE] border-[#FF5959] text-[#FF5959]'
                                                    : 'bg-[#ECF4FD] border-[#3F8EE8] text-[#3F8EE8]'
                                            }`}
                                        >
                                            {info.is_vendor ? 'Admin' : badge}
                                        </span>
                                    </div>
                                    <div className='flex flex-col'>
                                        <p className={`text-sm font-semibold ${userNameColor}`}>{userName}</p>
                                        <p className={`text-xs ${userEmailColor}`}>{userEmail}</p>
                                    </div>
                                </div>
                                <button
                                    type='button'
                                    onClick={handleLogout}
                                    className='mt-4 flex w-full items-center justify-between gap-3 rounded-full border border-gray-300 bg-white px-4 py-3 text-[16px] font-semibold text-base hover:bg-gray-50 cursor-pointer'
                                >
                                    <div className='flex items-center gap-2'>
                                        <img src={LogoutIcon} alt='' className='h-4 w-4' />
                                        <span>{logoutLabel}</span>
                                    </div>
                                    <ArrowIcon className='h-4 w-4' />
                                </button>
                            </>
                        ) : (
                            <div className='flex flex-col items-center gap-4'>
                                <LightTooltip title={t('Download.title')} placement='right'>
                                    <a
                                        href='/download'
                                        target='_blank'
                                        rel='noreferrer'
                                        aria-label={t('Download.title')}
                                        className='flex h-12 w-12 items-center justify-center rounded-full border border-gray-300 hover:bg-gray-50'
                                    >
                                        <img src='/download.svg' alt='' className='h-5 w-5' />
                                    </a>
                                </LightTooltip>
                                <LightTooltip title={helpLabel} placement='right'>
                                    <a
                                        href='https://www.gocochain.com/help'
                                        target='_blank'
                                        rel='noreferrer'
                                        aria-label={helpLabel}
                                        className='flex h-12 w-12 items-center justify-center rounded-full border border-gray-300 hover:bg-gray-50'
                                    >
                                        <img src='/help.svg' alt='' className='h-5 w-5' />
                                    </a>
                                </LightTooltip>
                                <div className='my-4 h-px w-full bg-gray-200' />
                                <div className='flex flex-col items-center gap-2'>
                                    <div className='relative flex h-12 w-12 items-center justify-center rounded-full border border-gray-300 bg-white'>
                                        <img src={logo} alt='User icon' className='h-6 w-6' />
                                        <span
                                            className={`absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/2 flex h-[18px] w-[53px] items-center justify-center rounded-full border text-[11px] font-semibold ${
                                                info.is_vendor
                                                    ? 'bg-[#FFEEEE] border-[#FF5959] text-[#FF5959]'
                                                    : 'bg-[#ECF4FD] border-[#3F8EE8] text-[#3F8EE8]'
                                            }`}
                                        >
                                            {info.is_vendor ? 'Admin' : badge}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type='button'
                                    onClick={handleLogout}
                                    className='flex h-12 w-12 items-center justify-center rounded-full border border-gray-300 hover:bg-gray-50 cursor-pointer'
                                    aria-label='Logout'
                                >
                                    <img src={LogoutIcon} alt='' className='h-5 w-5' />
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {isMobile && (
                    <div className='mt-auto flex flex-col gap-3'>
                        {/* Help button */}
                        <a
                            className='flex items-center justify-between gap-3 rounded-full border border-gray-400 bg-white px-4 py-3 text-[16px] font-semibold text-base'
                            href='https://www.gocochain.com/help'
                            target='_blank'
                            rel='noreferrer'
                            onClick={handleLinkClick}
                        >
                            <div className='flex items-center gap-2'>
                                <HelpIcon className='h-4 w-4' fill='#FFFFFF' />
                                <span>{helpLabel}</span>
                            </div>
                            <ArrowIcon className='h-4 w-4' fill='#FFFFFF' />
                        </a>
                        {/* Logout button */}
                        <button
                            type='button'
                            onClick={handleLogout}
                            className='flex items-center justify-between gap-3 rounded-full border border-gray-400 bg-white px-4 py-3 text-[16px] font-semibold text-base'
                        >
                            <div className='flex items-center gap-2'>
                                <img src={LogoutIcon} alt='' className='h-4 w-4' />
                                <span>{logoutLabel}</span>
                            </div>
                            <ArrowIcon className='h-4 w-4' fill='#FFFFFF' />
                        </button>
                    </div>
                )}
            </div>
            {isMobile && menuOpen ? (
                <div
                    aria-hidden='true'
                    className='fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm'
                    onClick={handleClose}
                />
            ) : null}
        </>
    );
}

export default MenuBar;
